<template>
  <div>
    <p>
      Enter the password you'd like to use to log in.
    </p>
    <p>
      <small>
        The password must be at least 8 characters and reasonably strong.
        Symbols and numbers can help but are not required, multiple words are
        better.
      </small>
    </p>
    <form-field type="password"
                label="Set password"
                :required="true"
                v-model="password"
                :validate="validatePassword"
                :focus="true"
                @submit="submit">
    </form-field>
    <div class="pw-strength"
         :class="[`score-${score}`, isShort && 'short']">
      <div class="pw-strength__bar">
        <div class="pw-strength__fill"
             :style="{ width: `${score*25}%` }">
        </div>
        <div class="pw-strength__sep"
             v-for="pos in [25, 50, 75]"
             :style="{ left: `calc(${pos}% - 2px)` }">
        </div>
      </div>
      <small>Estimated time to guess:</small>
      <strong v-if="!isEmpty" class="pw-strength__time">
        <small>{{crackTime}}</small>
      </strong>
    </div>
    <form-message v-if="feedback.hint"
                  severity="information"
                  :message="feedback.hint"/>
    <p v-if="hasSpaces">
      <small>
        Note: Spaces in passwords are ignored, so you can type your password
        with or without spaces when signing in.
      </small>
    </p>
    <form-button pull="right"
                 color="blue"
                 @click="submit">
      Submit
    </form-button>
  </div>
</template>

<script>
import zxcvbn from 'zxcvbn';
import { sanitizePassword } from '@/rules';

import FormButton from '../elements/FormButton';
import FormField from '../elements/FormField';
import FormMessage from '../elements/FormMessage';

export default {
  components: {
    FormButton,
    FormField,
    FormMessage,
  },

  data() {
    return {
      password: '',
      score: 0,
      isShort: true,
      isEmpty: true,
      hasSpaces: false,
      crackTime: '',
      feedback: {
        pending: false,
        hint: '',
      },
    };
  },

  methods: {
    scheduleFeedback({ warning, suggestions }) {
      const current = this.feedback;
      const hint = suggestions.reduce(
        (str, suggestion) => str + suggestion.replace(/[., ]*$/, '. '),
        warning && warning.replace(/[., ]*$/, '. '));

      if (current.hint) current.hint = '';
      if (current.pending) clearTimeout(current.pending);

      current.pending = setTimeout(() => {
        current.pending = false;
        current.hint = hint;
      }, 2000);
    },

    validatePassword(password) {
      const sanitized = sanitizePassword(password);

      // guesstimate strength, penalize welix etc...
      const strength = zxcvbn(sanitized, ['WElix', 'wesustain', 'weac']);

      this.score = strength.score;
      this.crackTime = strength.crack_times_display.offline_slow_hashing_1e4_per_second;
      this.hasSpaces = /\S\s+\S/.test(password);
      this.isEmpty = !password;
      this.isShort = sanitized.length < 8;

      this.scheduleFeedback(strength.feedback);

      return strength.score >= 3 && !this.isShort;
    },

    submit() {
      if (this.password) {
        this.$emit('submit', {
          password: sanitizePassword(this.password),
        });
      }
    },
  },
};
</script>

<style scoped>
.pw-strength {
  margin-top: -35px;
  margin-bottom: 10px;
  display: flex;
  flex-wrap: wrap;
}

.pw-strength__bar {
  position: relative;
  background-color: #e5e5e5;
  height: 4px;
  width: 100%;
  margin-bottom: 5px;
}
.pw-strength__sep {
  position: absolute;
  top: 0;
  height: 100%;
  width: 4px;
  background-color: #fff;
}

.pw-strength__fill {
  height: 100%;
  transition: background-color ease 0.5s, width ease 0.5s;
}

.short .pw-strength__fill,
.score-1 .pw-strength__fill,
.score-2 .pw-strength__fill {
  background-color: #ff4411 !important;
}
.score-3 .pw-strength__fill {
  background-color: #428bca;
}
.score-4 .pw-strength__fill {
  background-color: #11d15e;
}

.pw-strength__time {
  margin-left: auto;
}
.score-0 .pw-strength__time,
.score-1 .pw-strength__time,
.score-2 .pw-strength__time {
  color: #ff4411;
}
.score-3 .pw-strength__time {
  color: #428bca;
}
.score-4 .pw-strength__time {
  color: #11d15e;
}
</style>



// WEBPACK FOOTER //
// src/components/forms/Password.vue